import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Barcode, Eye, EyeSlash, GoogleLogo, MicrosoftOutlookLogo, Key, FileText, FilePdf, Presentation, DownloadSimple, Trophy, ArrowRight } from '@phosphor-icons/react';
import SocialProofStrip from '../components/SocialProofStrip';
import ThemeToggleButton from '../components/ThemeToggleButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [needs2fa, setNeeds2fa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoProviders, setSsoProviders] = useState([]);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API}/api/auth/sso/providers`)
      .then((r) => setSsoProviders(r.data.filter((p) => p.enabled)))
      .catch(() => setSsoProviders([]));
    const ssoError = searchParams.get('sso_error');
    if (ssoError) setError(`SSO failed: ${ssoError}`);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const totp = useRecovery ? null : (totpCode || null);
    const recovery = useRecovery ? (recoveryCode || null) : null;

    const result = await login(email, password, totp, recovery);
    if (result.success) {
      navigate('/');
    } else if (result.requires_2fa) {
      setNeeds2fa(true);
      setError('');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex relative">
      {/* Floating theme toggle for pre-auth pages */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggleButton />
      </div>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-brand-foreground flex items-center justify-center">
              <Barcode weight="bold" className="text-brand" size={28} />
            </div>
            <span className="text-brand-foreground font-bold text-3xl tracking-tight">STOCKARIS</span>
          </div>
          <p className="mt-4 text-brand-foreground/80 text-lg">Warehouse Management System</p>
        </div>
        <div className="text-brand-foreground/60 text-sm space-y-4">
          <SocialProofStrip variant="dark" />
          <div>
            <p>Streamline your warehouse operations</p>
            <p>with real-time inventory tracking</p>
          </div>
          {/* Prospect shortcut — same downloads as the right-panel strip,
              rendered here for people evaluating from LinkedIn or a cold
              email who won't ever click 'Sign up' first. */}
          <a
            href={`${API}/api/marketing/pitch-deck.pdf`}
            className="group inline-flex items-center gap-2 mt-2 px-3 py-2 border border-brand-foreground/25 hover:border-brand-foreground hover:bg-brand-foreground/5 transition-colors"
            data-testid="login-brand-pitch-deck"
          >
            <Presentation size={16} weight="fill" className="text-brand-foreground" />
            <span className="text-xs font-black uppercase tracking-widest text-brand-foreground">
              Investor Pitch Deck
            </span>
            <span className="text-[10px] font-mono text-brand-foreground/60 group-hover:text-brand-foreground/90">
              PDF · 8 slides · ANZ TAM
            </span>
          </a>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-md bg-brand flex items-center justify-center">
              <Barcode weight="bold" className="text-brand-foreground" size={24} />
            </div>
            <span className="font-bold text-2xl tracking-tight text-foreground">STOCKARIS</span>
          </div>

          {/* Competition Demo — one-tap access for judges landing on the sign-in page */}
          <Link
            to="/competition-demo"
            data-testid="login-competition-demo-cta"
            className="group mb-5 flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/15 hover:border-emerald-500 transition-colors"
          >
            <span className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 rounded-md bg-emerald-500 flex items-center justify-center shrink-0">
                <Trophy size={18} weight="duotone" className="text-emerald-950" />
              </span>
              <span className="flex flex-col min-w-0">
                <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-500">
                  Competition Judge
                </span>
                <span className="text-sm font-semibold text-foreground truncate">
                  Enter the Warehouse Challenge demo
                </span>
              </span>
            </span>
            <ArrowRight size={18} className="text-emerald-500 group-hover:translate-x-0.5 transition-transform shrink-0" />
          </Link>

          <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
            <h1 className="text-2xl font-semibold mb-1 text-foreground tracking-tight">{t('common.welcome_back', 'Welcome back')}</h1>
            <p className="text-muted-foreground mb-6 text-sm">Sign in to your account</p>

            {error && (
              <div data-testid="login-error" className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="overline">{t('common.email', 'Email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@stockaris.com"
                  data-testid="login-email"
                  className="mt-1"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password" className="overline">{t('common.password', 'Password')}</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid="login-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="w-full font-semibold py-3"
              >
                {loading ? t('common.signing_in', 'Signing in...') : needs2fa ? 'Verify & Sign In' : t('common.sign_in', 'Sign In')}
              </Button>

              {!needs2fa && (
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground" data-testid="login-forgot-password">
                    Forgot password?
                  </Link>
                </div>
              )}

              {needs2fa && (
                <div data-testid="2fa-login-step" className="border-t border-border pt-4 mt-4 space-y-3">
                  <p className="text-sm font-semibold text-foreground">
                    Two-factor authentication required
                  </p>
                  {!useRecovery ? (
                    <div>
                      <Label className="overline">6-digit code from your authenticator</Label>
                      <Input
                        type="text"
                        maxLength={6}
                        inputMode="numeric"
                        placeholder="000000"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                        className="mt-1 font-mono text-2xl text-center tracking-widest"
                        data-testid="login-totp-code"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div>
                      <Label className="overline">Recovery code</Label>
                      <Input
                        type="text"
                        placeholder="XXXX-XXXX"
                        value={recoveryCode}
                        onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                        className="mt-1 font-mono"
                        data-testid="login-recovery-code"
                        autoFocus
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setUseRecovery(!useRecovery)}
                    className="text-xs text-brand font-semibold uppercase tracking-wider"
                    data-testid="toggle-recovery"
                  >
                    {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
                  </button>
                </div>
              )}
            </form>

            {ssoProviders.length > 0 && !needs2fa && (
              <div className="mt-5" data-testid="sso-providers">
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-card px-2 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                      or continue with
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {ssoProviders.map((p) => {
                    const Icon = p.key === 'google' || p.key === 'emergent_google' ? GoogleLogo : p.key === 'microsoft' ? MicrosoftOutlookLogo : Key;
                    if (p.type === 'emergent') {
                      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                      const redirectUrl = window.location.origin + '/';
                      const href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                      return (
                        <a
                          key={p.key}
                          href={href}
                          data-testid={`sso-${p.key}`}
                          className="flex items-center justify-center gap-2 w-full py-2.5 border border-border rounded-md hover:bg-accent text-foreground font-semibold text-sm transition-colors"
                        >
                          <Icon size={18} weight="bold" />
                          Continue with Google
                        </a>
                      );
                    }
                    // SAML providers have "saml:okta" style keys; strip prefix for URL
                    const href = p.type === 'saml'
                      ? `${API}/api/auth/saml/login/${p.key.replace('saml:', '')}`
                      : `${API}/api/auth/sso/login/${p.key}`;
                    return (
                      <a
                        key={p.key}
                        href={href}
                        data-testid={`sso-${p.key.replace(':', '-')}`}
                        className="flex items-center justify-center gap-2 w-full py-2.5 border border-border rounded-md hover:bg-accent text-foreground font-semibold text-sm transition-colors"
                      >
                        <Icon size={18} weight="bold" />
                        Continue with {p.name}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="mt-6 text-center text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-brand font-semibold hover:underline" data-testid="login-to-signup">
                Sign up
              </Link>
              {' · '}
              <Link to="/request-demo" className="text-brand font-semibold hover:underline" data-testid="login-to-demo">
                Request a demo
              </Link>
            </p>

            {/* Prospect resources — pitch deck + capability specs. Rendered
                as a compact 3-up strip so it doesn't compete with the
                primary sign-in CTA above. */}
            <div className="mt-5 pt-5 border-t border-border" data-testid="login-resources-strip">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground text-center mb-3">
                Evaluating Stockaris? Grab the specs.
              </div>
              <div className="grid grid-cols-3 gap-2">
                <a
                  href={`${API}/api/marketing/pitch-deck.pdf`}
                  className="group flex flex-col items-center justify-center gap-1 py-2.5 border border-emerald-600/25 hover:border-emerald-600 hover:bg-emerald-600/5 transition-colors"
                  data-testid="login-resource-pitch-deck"
                  title="8-slide investor + enterprise pitch deck (PDF)"
                >
                  <Presentation size={20} weight="fill" className="text-emerald-700" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Pitch Deck</span>
                  <span className="text-[9px] text-muted-foreground">PDF · 8 slides</span>
                </a>
                <a
                  href={`${API}/api/marketing/capabilities.docx`}
                  className="group flex flex-col items-center justify-center gap-1 py-2.5 border border-brand/25 hover:border-brand hover:bg-brand/5 transition-colors"
                  data-testid="login-resource-capabilities"
                  title="Full capabilities dossier (Word)"
                >
                  <FileText size={20} weight="fill" className="text-brand" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-brand">Full Specs</span>
                  <span className="text-[9px] text-muted-foreground">Word · 19 sections</span>
                </a>
                <a
                  href={`${API}/api/marketing/onepager.pdf`}
                  className="group flex flex-col items-center justify-center gap-1 py-2.5 border border-amber-500/25 hover:border-amber-500 hover:bg-amber-500/5 transition-colors"
                  data-testid="login-resource-onepager"
                  title="One-page cold-outreach summary (PDF)"
                >
                  <FilePdf size={20} weight="fill" className="text-amber-600" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-amber-700">One-Pager</span>
                  <span className="text-[9px] text-muted-foreground">PDF · 1 page</span>
                </a>
              </div>
              <div className="mt-3 text-center">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-brand"
                  data-testid="login-resource-see-all"
                >
                  <DownloadSimple size={11} weight="bold" /> See pricing &amp; all resources
                </Link>
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
